import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, User, Settings, Users, UsersRound, Sun, Moon, Heart, Briefcase, Sparkles, UserCircle2, ChevronDown, Waves, LayoutDashboard, Ticket, HandHeart, HeartHandshake, Wallet , Store , Receipt, CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useState } from 'react';
import { getImageUrl } from '@/lib/imageUtils';
import { Link } from 'react-router-dom';

// Demo users with their profiles - these will be clickable
const DEMO_CONNECTIONS = {
  all: [
    { username: 'marcus_chen', name: 'Marcus Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop' },
    { username: 'priya_sharma', name: 'Priya Sharma', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop' },
    { username: 'elena_martinez', name: 'Elena Martinez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop' },
    { username: 'jordan_brooks', name: 'Jordan Brooks', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop' },
    { username: 'natalie_ross', name: 'Natalie Ross', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop' },
    { username: 'alex_turner', name: 'Alex Turner', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop' },
    { username: 'sofia_liu', name: 'Sofia Liu', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop' },
    { username: 'david_kim', name: 'David Kim', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop' },
    { username: 'maya_patel', name: 'Maya Patel', avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop' },
    { username: 'BryanFerre', name: 'Bryan Ferre', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=100&h=100&fit=crop' },
  ],
  friends: [
    { username: 'marcus_chen', name: 'Marcus Chen', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop' },
    { username: 'maya_patel', name: 'Maya Patel', avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&h=100&fit=crop' },
    { username: 'alex_turner', name: 'Alex Turner', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop' },
    { username: 'natalie_ross', name: 'Natalie Ross', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop' },
    { username: 'sofia_liu', name: 'Sofia Liu', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop' },
    { username: 'david_kim', name: 'David Kim', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop' },
    { username: 'jordan_brooks', name: 'Jordan Brooks', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop' },
    { username: 'elena_martinez', name: 'Elena Martinez', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop' },
  ],
  best_friends: [
    { username: 'priya_sharma', name: 'Priya Sharma', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop' },
    { username: 'natalie_ross', name: 'Natalie Ross', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop' },
    { username: 'sofia_liu', name: 'Sofia Liu', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop' },
  ],
  family: [
    { username: 'MorganFerre', name: 'Morgan Ferre', avatar: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100&h=100&fit=crop' },
    { username: 'BryanFerre', name: 'Bryan Ferre', avatar: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=100&h=100&fit=crop' },
  ],
  colleagues: [
    { username: 'david_kim', name: 'David Kim', avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop' },
    { username: 'elena_martinez', name: 'Elena Martinez', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop' },
  ],
  like_minded: [
    { username: 'priya_sharma', name: 'Priya Sharma', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop' },
  ],
};

const CONNECTION_TYPES = [
  { id: 'all', label: 'All', icon: Users, color: 'bg-stone-500', count: 16 },
  { id: 'friends', label: 'Friends', icon: UserCircle2, color: 'bg-blue-500', count: 8 },
  { id: 'best_friends', label: 'Best Friends', icon: Heart, color: 'bg-pink-500', count: 3 },
  { id: 'family', label: 'Family', icon: Users, color: 'bg-orange-500', count: 2 },
  { id: 'colleagues', label: 'Colleagues', icon: Briefcase, color: 'bg-slate-500', count: 2 },
  { id: 'like_minded', label: 'Like Minded', icon: Sparkles, color: 'bg-purple-500', count: 1 },
];

export const Sidebar = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeConnectionType, setActiveConnectionType] = useState('all');

  const navItems = [
    { icon: Home, label: 'Dashboard', to: '/dashboard' },
    { icon: Search, label: 'Explore', to: '/' },
    { icon: HandHeart, label: 'Donate', to: '/donate' },
    { icon: Users, label: 'Connections', to: '/discover' },
    { icon: HeartHandshake, label: 'Causes', to: '/spaces' },
    { icon: Store, label: 'Businesses', to: '/businesses' },
    { icon: CalendarDays, label: 'Events', to: '/events' },
    { icon: Receipt, label: 'Your activity', to: '/my/activity' },
    { icon: Wallet, label: 'Wallet', to: '/wallet' },
    { icon: Ticket, label: 'Invites', to: '/invites' },
  ];

  return (
    <aside className="hidden lg:flex flex-col w-72 border-r border-stone-300 dark:border-white/5 h-[calc(100vh-64px)] sticky top-16 bg-white dark:bg-zinc-900/50 overflow-y-auto scrollbar-hide">
      {/* Navigation */}
      <nav className="p-4 space-y-1">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={label}
            to={to}
            data-testid={`sidebar-${label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm',
                'hover:bg-stone-100/80 dark:hover:bg-white/5',
                isActive ? 'bg-stone-100 dark:bg-white/10 text-stone-900 dark:text-white font-medium' : 'text-stone-600 dark:text-zinc-400'
              )
            }
          >
            <Icon className="w-5 h-5" strokeWidth={1.5} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* My Connections Section */}
      <div className="p-4 border-t border-stone-300 dark:border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-stone-800 dark:text-white">My Connections</h3>
          <span className="text-xs text-stone-400 dark:text-zinc-500">16</span>
        </div>
        
        {/* Connection Type Filter */}
        <ConnectionTypeFilter onTypeChange={setActiveConnectionType} />
        
        {/* Avatars - clickable links to profiles */}
        <div className="grid grid-cols-5 gap-2 mt-3">
          {(DEMO_CONNECTIONS[activeConnectionType] || DEMO_CONNECTIONS.all).map((connection, i) => (
            <Link 
              key={`${activeConnectionType}-${i}`} 
              to={`/profile/${connection.username}`}
              title={connection.name}
              className="block"
            >
              <Avatar className="w-9 h-9 cursor-pointer hover:ring-2 hover:ring-gathrr-400 dark:hover:ring-gathrr-500 transition-all hover:scale-105">
                <AvatarImage src={connection.avatar} alt={connection.name} />
                <AvatarFallback className="bg-stone-100 dark:bg-zinc-700 text-stone-700 dark:text-zinc-300 text-xs">
                  {connection.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </Link>
          ))}
        </div>
      </div>

      {/* Impact & Settings at bottom */}
      <div className="mt-auto p-4 border-t border-stone-300 dark:border-white/5 space-y-1">
        <NavLink
          to="/impact"
          data-testid="sidebar-impact"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm',
              'hover:bg-stone-100/80 dark:hover:bg-white/5',
              isActive ? 'bg-stone-100 dark:bg-white/10 text-stone-900 dark:text-white font-medium' : 'text-stone-600 dark:text-zinc-400'
            )
          }
        >
          <Waves className="w-5 h-5" strokeWidth={1.5} />
          <span>Impact</span>
        </NavLink>
        <NavLink
          to="/settings"
          data-testid="sidebar-settings"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm',
              'hover:bg-stone-100/80 dark:hover:bg-white/5',
              isActive ? 'bg-stone-100 dark:bg-white/10 text-stone-900 dark:text-white font-medium' : 'text-stone-600 dark:text-zinc-400'
            )
          }
        >
          <Settings className="w-5 h-5" strokeWidth={1.5} />
          <span>Settings</span>
        </NavLink>
      </div>
    </aside>
  );
};

const ConnectionTypeFilter = ({ onTypeChange }) => {
  const [activeType, setActiveType] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);
  
  const activeConnection = CONNECTION_TYPES.find(c => c.id === activeType);
  
  const handleTypeChange = (typeId) => {
    setActiveType(typeId);
    setIsExpanded(false);
    if (onTypeChange) {
      onTypeChange(typeId);
    }
  };
  
  return (
    <div className="relative">
      {/* Selected Type Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-stone-100 dark:bg-zinc-800/50 rounded-lg hover:bg-stone-200 dark:hover:bg-zinc-700/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={cn('w-2 h-2 rounded-full', activeConnection?.color)} />
          <span className="text-sm text-stone-700 dark:text-zinc-300">{activeConnection?.label}</span>
        </div>
        <ChevronDown className={cn('w-4 h-4 text-stone-400 dark:text-zinc-500 transition-transform', isExpanded && 'rotate-180')} />
      </button>
      
      {/* Dropdown */}
      {isExpanded && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-zinc-900 border border-stone-200 dark:border-white/10 rounded-xl shadow-lg overflow-hidden z-20">
          {CONNECTION_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => {
                  handleTypeChange(type.id);
                }}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2.5 hover:bg-stone-50 dark:hover:bg-zinc-800/50 transition-colors',
                  activeType === type.id && 'bg-stone-50 dark:bg-zinc-800/50'
                )}
              >
                <div className="flex items-center gap-2.5">
                  <div className={cn('w-6 h-6 rounded-lg flex items-center justify-center text-white', type.color)}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <span className="text-sm text-stone-700 dark:text-zinc-300">{type.label}</span>
                </div>
                <span className="text-xs text-stone-400 dark:text-zinc-500 bg-white border border-stone-200 dark:bg-zinc-800 dark:border-white/10 px-2 py-0.5 rounded-full">
                  {type.count}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
